import type { Permission } from '@elsa/contracts';
import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { rpc, supabase } from './supabase.ts';
import type { Entitlements, Org } from './types.ts';

// Sesiunea si firma curenta. Firmele, cu rolul si permisiunile din fiecare, vin din
// app.my_orgs(); firma aleasa se tine minte in localStorage. Drepturile din UI (can) sunt doar
// pentru afisare (meniul, butoanele): aceleasi reguli le aplica SQL-ul la fiecare apel.

const ORG_KEY = 'elsa.org';

export function useAuthSession(): Session | null | undefined {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);
  return session;
}

export function useOrgs(userId: string | undefined) {
  return useQuery({
    queryKey: ['orgs', userId],
    enabled: Boolean(userId),
    queryFn: () => rpc<Org[]>('my_orgs'),
  });
}

interface Ctx {
  session: Session;
  orgs: Org[];
  org: Org;
  setOrg: (id: string) => void;
  entitlements: Entitlements | undefined;
  /** Drepturile firmei curente au venit (sau au esuat): ecranul de pornire se poate stinge. */
  entitlementsSettled: boolean;
  can: (p: Permission) => boolean;
}

const OrgContext = createContext<Ctx | null>(null);

export function OrgProvider({
  session,
  orgs,
  children,
}: {
  session: Session;
  orgs: Org[];
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState(() => {
    try {
      return localStorage.getItem(ORG_KEY);
    } catch {
      return null;
    }
  });
  const org = orgs.find((o) => o.id === current) ?? (orgs[0] as Org);
  const ent = useQuery({
    queryKey: ['entitlements', org.id],
    queryFn: () => rpc<Entitlements>('entitlements', { p_org: org.id }),
  });
  const value = useMemo<Ctx>(
    () => ({
      session,
      orgs,
      org,
      entitlements: ent.data,
      entitlementsSettled: !ent.isPending,
      setOrg: (id) => {
        try {
          localStorage.setItem(ORG_KEY, id);
        } catch {
          /* fara localStorage: alegerea tine pana la reincarcare */
        }
        setCurrent(id);
        void qc.invalidateQueries();
      },
      can: (p) => org.permissions.includes(p),
    }),
    [session, orgs, org, ent.data, ent.isPending, qc],
  );
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): Ctx {
  const c = useContext(OrgContext);
  if (!c) throw new Error('useOrg in afara OrgProvider');
  return c;
}

export function rememberOrg(id: string): void {
  try {
    localStorage.setItem(ORG_KEY, id);
  } catch {
    /* ignorat */
  }
}
